import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BarChart3, ArrowRight, Copy, Share2, CheckCircle2, Wallet, Paintbrush } from "lucide-react";
import { toast } from "sonner";
import usePageMeta from "../../hooks/usePageMeta";
import { api, track } from "../../lib/api";

const THEME_OPTIONS = [
  { id: "classic", label: "Classic Farevault", swatch: "bg-gradient-to-br from-[#00172D] to-[#970C10]" },
  { id: "dark-luxe", label: "Dark Luxe", swatch: "bg-[#0B0B10] ring-1 ring-amber-400/60" },
  { id: "gulf-gold", label: "Gulf Gold", swatch: "bg-gradient-to-br from-[#6B4E12] to-[#B8860B]" },
  { id: "fresh-teal", label: "Fresh Teal", swatch: "bg-gradient-to-br from-[#042F2E] to-[#0D9488]" },
];

function CustomizePanel({ code, affiliate, onSaved, initialToken }) {
  const [theme, setTheme] = useState(affiliate.theme || "classic");
  const [headline, setHeadline] = useState(affiliate.custom_hero_headline || "");
  const [subtext, setSubtext] = useState(affiliate.custom_hero_subtext || "");
  const [token, setToken] = useState(initialToken || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (token.trim().length < 10) { toast.error("Enter your edit token (shown when you signed up)"); return; }
    setSaving(true);
    try {
      await api.post(`/affiliates/${code}/hero`, {
        token: token.trim(), theme,
        custom_hero_headline: headline, custom_hero_subtext: subtext,
      });
      toast.success("Your landing page has been updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.status === 403 ? "Invalid edit token" : (e.response?.data?.detail || "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10" data-testid="affiliate-customize-section">
      <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
        <Paintbrush className="w-5 h-5 text-[#970C10]" /> Customize my landing page
      </h2>
      <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-5 space-y-5">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Theme</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {THEME_OPTIONS.map((t) => (
              <button
                key={t.id}
                data-testid={`affiliate-theme-${t.id}`}
                onClick={() => setTheme(t.id)}
                className={`rounded-xl border-2 p-2 text-left transition-colors duration-200 ${theme === t.id ? "border-[#970C10]" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className={`h-10 rounded-lg ${t.swatch}`} />
                <p className="text-[11px] font-bold text-slate-700 mt-1.5">{t.label}</p>
              </button>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Custom headline (optional)</p>
            <input data-testid="affiliate-headline-input" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#970C10]/30" maxLength={200} value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder={`Cheap flights hand-picked for ${affiliate.name}'s community`} />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Custom subtext (optional)</p>
            <input data-testid="affiliate-subtext-input" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#970C10]/30" maxLength={500} value={subtext} onChange={(e) => setSubtext(e.target.value)} placeholder="Live discounted fares, instant e-tickets, WhatsApp support" />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Your edit token</p>
            <input data-testid="affiliate-token-input" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#970C10]/30" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Shown when you signed up — ask us on WhatsApp if lost" />
          </div>
          <button
            data-testid="affiliate-customize-save-btn"
            onClick={save}
            disabled={saving}
            className="bg-[#970C10] hover:bg-[#7A0A0D] disabled:opacity-40 text-white font-bold text-sm px-6 py-2.5 rounded-lg transition-colors duration-200"
          >
            {saving ? "Saving…" : "Save & apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AffiliateStats() {
  const { code } = useParams();
  const [stats, setStats] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [affToken, setAffToken] = useState(() => {
    try { return localStorage.getItem(`sv_aff_token_${code}`) || ""; } catch { return ""; }
  });
  const [unlockInput, setUnlockInput] = useState("");

  const fetchStats = (tok) =>
    api.get(`/affiliates/${code}/stats${tok ? `?token=${encodeURIComponent(tok)}` : ""}`);

  useEffect(() => {
    fetchStats(affToken)
      .then((r) => setStats(r.data))
      .catch((e) => {
        if (e.response?.status === 403 && affToken) {
          try { localStorage.removeItem(`sv_aff_token_${code}`); } catch { /* noop */ }
          setAffToken("");
          fetchStats("").then((r) => setStats(r.data)).catch(() => setNotFound(true));
        } else {
          setNotFound(true);
        }
      });
    track("affiliate_stats_view", `/a/${code}/stats`, { code });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const reload = () => fetchStats(affToken).then((r) => setStats(r.data)).catch(() => {});

  const unlock = () => {
    const t = unlockInput.trim();
    if (t.length < 10) { toast.error("Enter your edit token (shown when you signed up)"); return; }
    fetchStats(t)
      .then((r) => {
        try { localStorage.setItem(`sv_aff_token_${code}`, t); } catch { /* noop */ }
        setAffToken(t);
        setStats(r.data);
        toast.success("Earnings unlocked");
      })
      .catch(() => toast.error("Invalid token"));
  };

  usePageMeta(
    stats ? `Stats — ${stats.affiliate.name} | Sellvago Affiliate` : "Affiliate Stats",
    "Live click, search and booking-handoff counts for your Sellvago affiliate link."
  );

  const landingUrl = typeof window !== "undefined" ? `${window.location.origin}/a/${code}` : `/a/${code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(landingUrl);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Copy failed — long-press to select the link");
    }
  };

  const share = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: "My Farevault affiliate link", url: landingUrl });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="affiliate-stats-not-found">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Affiliate not found</h1>
        <Link to="/affiliate-program" className="mt-6 inline-flex items-center gap-2 text-[#970C10] font-bold text-sm">
          Apply to the affiliate program <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }
  if (!stats) return <div className="min-h-[400px] flex items-center justify-center text-slate-400 text-sm">Loading…</div>;

  const cards = [
    { l: "Landing views", v: stats.landing_views, t: "Total unique visits to your /a/ link" },
    { l: "Searches started", v: stats.searches, t: "Visitors who submitted a search on your page" },
    { l: "Booking handoffs", v: stats.booking_handoffs, t: "Visitors who clicked through to book on our engine" },
    { l: "WhatsApp clicks", v: stats.whatsapp_clicks, t: "Visitors who tapped WhatsApp support" },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14" data-testid="affiliate-stats-page">
      <p className="text-xs uppercase tracking-[0.2em] text-[#E8767A] font-bold mb-3">Affiliate Dashboard</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900">
        {stats.affiliate.name}
      </h1>
      <p className="text-slate-500 text-sm mt-2 font-mono">code: <span className="text-slate-800 font-bold">{code}</span> · status: <span className="text-slate-800 font-bold">{stats.affiliate.status}</span></p>

      <div className="mt-6 bg-slate-900 text-white rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">Your affiliate link</p>
          <p className="font-mono text-xs sm:text-sm truncate" data-testid="affiliate-landing-url">{landingUrl}</p>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} data-testid="affiliate-copy-btn" className="inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2 rounded-lg transition-colors duration-200">
            <Copy className="w-3.5 h-3.5" /> Copy
          </button>
          <button onClick={share} data-testid="affiliate-share-btn" className="inline-flex items-center gap-1.5 bg-[#970C10] hover:bg-[#7A0A0D] text-white text-xs font-bold px-3 py-2 rounded-lg transition-colors duration-200">
            <Share2 className="w-3.5 h-3.5" /> Share
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-8" data-testid="affiliate-stats-grid">
        {cards.map((c) => (
          <div key={c.l} className="bg-white border border-slate-200 rounded-2xl p-5">
            <p className="font-heading text-3xl font-bold text-slate-900">{c.v}</p>
            <p className="text-xs uppercase tracking-wide text-slate-500 mt-1 font-bold">{c.l}</p>
            <p className="text-xs text-slate-400 mt-1">{c.t}</p>
          </div>
        ))}
      </div>

      {stats.earnings_locked && (
        <div className="mt-10" data-testid="affiliate-earnings-locked">
          <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#970C10]" /> Earnings
          </h2>
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-5">
            <p className="text-sm text-slate-600 font-semibold">Your earnings are private.</p>
            <p className="text-xs text-slate-500 mt-1">Enter your edit token (shown when you signed up) to unlock commission totals and payout history on this device.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <input
                data-testid="affiliate-unlock-token-input"
                className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#970C10]/30"
                value={unlockInput}
                onChange={(e) => setUnlockInput(e.target.value)}
                placeholder="Your edit token — ask us on WhatsApp if lost"
              />
              <button
                data-testid="affiliate-unlock-btn"
                onClick={unlock}
                className="bg-[#970C10] hover:bg-[#7A0A0D] text-white font-bold text-sm px-6 py-2.5 rounded-lg transition-colors duration-200"
              >
                Unlock earnings
              </button>
            </div>
          </div>
        </div>
      )}

      {stats.earnings && (
        <div className="mt-10" data-testid="affiliate-earnings-section">
          <h2 className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#970C10]" /> Earnings
          </h2>
          <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-4">
            <div className="bg-slate-900 text-white rounded-2xl p-4 sm:p-5">
              <p className="font-heading text-xl sm:text-2xl font-bold" data-testid="affiliate-total-earned">PKR {stats.earnings.total_earned_pkr.toLocaleString()}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-400 mt-1 font-bold">Total earned</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
              <p className="font-heading text-xl sm:text-2xl font-bold text-green-700" data-testid="affiliate-paid-out">PKR {stats.earnings.paid_pkr.toLocaleString()}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 mt-1 font-bold">Paid out</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5">
              <p className="font-heading text-xl sm:text-2xl font-bold text-amber-600" data-testid="affiliate-pending">PKR {stats.earnings.pending_pkr.toLocaleString()}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wide text-slate-500 mt-1 font-bold">Pending payout</p>
            </div>
          </div>

          {stats.payout_history?.length > 0 ? (
            <div className="mt-5 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="affiliate-payout-history">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5">Booking</th>
                    <th className="px-4 py-2.5">Route</th>
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5 text-right">Commission</th>
                    <th className="px-4 py-2.5 text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.payout_history.map((b, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`affiliate-payout-row-${i}`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{b.ref}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{b.route || "—"}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500">{b.date ? new Date(b.date).toLocaleDateString() : "—"}</td>
                      <td className="px-4 py-2.5 text-right font-bold">PKR {b.commission_pkr.toLocaleString()}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${b.payout_status === "paid" ? "bg-green-100 text-green-700" : b.payout_status === "reversed" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>
                          {b.payout_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="mt-4 text-xs text-slate-400" data-testid="affiliate-no-payouts">No reconciled bookings yet — commissions appear here after each monthly reconciliation cycle.</p>
          )}
        </div>
      )}

      <CustomizePanel key={stats.affiliate.theme + code + affToken} code={code} affiliate={stats.affiliate} onSaved={reload} initialToken={affToken} />

      <div className="mt-8 bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl p-5 flex gap-3">
        <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold">Commission reconciliation</p>
          <p className="text-xs mt-1 opacity-90">{stats.commission_notice}</p>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
        <BarChart3 className="w-3.5 h-3.5" /> Data refreshes on page load. Bookmark this URL for quick access.
      </div>
    </div>
  );
}
